/* global fetch:false */
import Cookies from 'js-cookie';
import {
  ADD_TO_WISHLIST_PENDING,
  fetchAccountDetailsActionCreator,
  REHYDRATED,
  REMOVE_FROM_WISHLIST_PENDING,
  synchronizeWishlistActionCreator,
} from '../actions';
import { getEveApiAccessToken, getEveApiUrl, isGuestAccount } from '../selectors';
import get from '../helpers/get';
import getLocalStorageObject from '../helpers/getLocalStorageObject';

const AUTH_KEY = 'ngStorage-ti_auth';
const EVE_TOKEN_COOKIE_NAME = 'eve_access';
/**
 * Delay before retrying account fetch to allow external login scripts
 * to complete updating localStorage. Increase if login persistence fails.
 */
export const ACCOUNT_FETCH_RETRY_DELAY_MS = 500;

const getCachedAuth = () => getLocalStorageObject(AUTH_KEY) || { isGuest: true };

const accountSnapshotEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const fetchAccount = async () => {
  const result = {
    details: getCachedAuth(),
  };
  if (!result.details.isGuest) {
    result.token = get(JSON.parse(Cookies.get(EVE_TOKEN_COOKIE_NAME)), ['user']);
  }
  return result;
};

const sendWishlistRequest = async (url, token, { headers, ...options }) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = new Error('Failed to synchronise account wishlist');
    error.status = response.status;
    throw error;
  }
};

const requestAddItemToWishlist = async (apiUrl, token, sku) => {
  const url = [apiUrl, 'customer', 'wishlist'].join('/');
  const body = `configurable_sku=${sku}`;
  return sendWishlistRequest(url, token, {
    method: 'POST',
    body,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    },
  });
};

const requestRemoveItemFromWishlist = async (apiUrl, token, sku) => {
  const url = [apiUrl, 'customer', 'wishlist', sku].join('/');
  return sendWishlistRequest(url, token, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
    },
  });
};

const dispatchSynchronizeAction = (store, action) => {
  const state = store.getState();
  const apiUrl = getEveApiUrl(state);
  const token = getEveApiAccessToken(state);
  const { sku } = action.meta;

  switch (action.type) {
    case ADD_TO_WISHLIST_PENDING:
      store.dispatch(synchronizeWishlistActionCreator(
        requestAddItemToWishlist(apiUrl, token, sku),
      ));
      break;
    case REMOVE_FROM_WISHLIST_PENDING:
      store.dispatch(synchronizeWishlistActionCreator(
        requestRemoveItemFromWishlist(apiUrl, token, sku),
      ));
      break;
    // no default
  }
};

export default store => next => action => {
  const ret = next(action);
  switch (action.type) {
    case REHYDRATED: {
      const firstPromise = fetchAccount();
      store.dispatch(fetchAccountDetailsActionCreator(firstPromise));

      // Retry after delay to catch updates from external login scripts
      // that may update localStorage asynchronously after initial load.
      // Skip the second dispatch when nothing changed (avoids duplicate fetches).
      setTimeout(async () => {
        const firstResult = await firstPromise;
        const secondResult = await fetchAccount();
        if (accountSnapshotEqual(firstResult, secondResult)) {
          return;
        }
        store.dispatch(fetchAccountDetailsActionCreator(Promise.resolve(secondResult)));
      }, ACCOUNT_FETCH_RETRY_DELAY_MS);

      break;
    }
    case ADD_TO_WISHLIST_PENDING:
    case REMOVE_FROM_WISHLIST_PENDING:
      if (!isGuestAccount(store.getState())) {
        dispatchSynchronizeAction(store, action);
      }
      break;
    // no default
  }
  return ret;
};
